import React, { useCallback, useEffect, useState } from 'react';
import classNames from 'classnames';
import ColorCircle from '../color-circle';
import HorizontalStack from '../horizontal-stack';
import ActionItem from './components/action-item';
import InteractiveBox from './components/interactive-box';
import { SIZES } from './constants';

const getColorIndexById = (colors, id) => colors.findIndex((color) => color.id === id);

const moveColorToBeginningById = (colors, id) => [
  colors[getColorIndexById(colors, id)],
  ...colors.filter((color) => color.id !== id),
];

const ColorSelector = ({
  size = SIZES.large,
  items,
  selectedItemId = undefined,
  visibleItemsCount = 4,
  onClick,
  onAddButtonClick = undefined,
}) => {
  const classes = classNames('ws-ui-color-selector', `ws-ui-color-selector--${size}`);
  const isShowMoreEnabled = items.length > visibleItemsCount;
  const initialColors =
    selectedItemId && getColorIndexById(items, selectedItemId) > visibleItemsCount
      ? moveColorToBeginningById(items, selectedItemId)
      : items;

  const [isCollapsed, setIsCollapsed] = useState(true);
  const [colors, setColors] = useState(initialColors);
  const [lastVisibleIndex, setVisibleIndex] = useState(visibleItemsCount);

  const moveSelectedColorToBeginning = useCallback(() => {
    if (!selectedItemId) {
      return;
    }

    const selectedItemIndex = getColorIndexById(colors, selectedItemId);
    const isSelectedItemVisible = selectedItemIndex < visibleItemsCount;

    if (!isSelectedItemVisible) {
      setColors(moveColorToBeginningById(colors, selectedItemId));
    }
  }, [colors, selectedItemId, visibleItemsCount]);

  useEffect(() => {
    if (isCollapsed) {
      moveSelectedColorToBeginning();
      setVisibleIndex(visibleItemsCount);
    } else {
      setVisibleIndex(colors.length);
    }
  }, [colors, isCollapsed, moveSelectedColorToBeginning, visibleItemsCount]);

  return (
    <div className={classes}>
      <HorizontalStack gap="spacing-3-xs" rowGap="spacing-3-xs" wrap>
        {colors.slice(0, lastVisibleIndex).map((color) => (
          <InteractiveBox
            key={color.id}
            selected={color.id === selectedItemId}
            onClick={() => {
              onClick(color);
            }}
          >
            <ColorCircle {...color} size="stretch" />
          </InteractiveBox>
        ))}
        {isShowMoreEnabled && (
          <InteractiveBox
            onClick={() => {
              setIsCollapsed((state) => !state);
            }}
          >
            <ActionItem size={size} iconGlyph={isCollapsed ? 'next' : 'back'} />
          </InteractiveBox>
        )}
        {onAddButtonClick && (
          <InteractiveBox onClick={onAddButtonClick}>
            <ActionItem size={size} iconGlyph="plus-for-button" />
          </InteractiveBox>
        )}
      </HorizontalStack>
    </div>
  );
};

export default ColorSelector;
